import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from './apiClient';
import { roundMoney2 } from './utils/money';

const PrintingContext = createContext();

export function usePrinting() {
  return useContext(PrintingContext);
}

export function PrintingProvider({ children }) {
  const { user } = useAuth();
  const [printJobs, setPrintJobs] = useState([]);
  const [RATE_BW, setRateBw] = useState(100);
  const [RATE_COLOR, setRateColor] = useState(300);
  const [RATE_COPY, setRateCopy] = useState(150);
  const [RATE_SCAN, setRateScan] = useState(200);
  const [RATE_CUSTOMER_REAM, setRateCustomerReam] = useState(80);

  const refresh = useCallback(async () => {
    try {
      const data = await apiJson('/print-jobs');
      setPrintJobs(Array.isArray(data.printJobs) ? data.printJobs : []);
      if (data.RATE_BW != null) setRateBw(roundMoney2(data.RATE_BW));
      if (data.RATE_COLOR != null) setRateColor(roundMoney2(data.RATE_COLOR));
      if (data.RATE_COPY != null) setRateCopy(roundMoney2(data.RATE_COPY));
      if (data.RATE_SCAN != null) setRateScan(roundMoney2(data.RATE_SCAN));
      if (data.RATE_CUSTOMER_REAM != null) setRateCustomerReam(roundMoney2(data.RATE_CUSTOMER_REAM));
    } catch {
      setPrintJobs([]);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setPrintJobs([]);
      return;
    }
    void refresh();
  }, [user, refresh]);

  const addPrintJob = useCallback(
    async ({ pages, printType, createdBy: _ignored }) => {
      const data = await apiJson('/print-jobs', {
        method: 'POST',
        body: JSON.stringify({
          pages: Number(pages),
          printType,
        }),
      });
      await refresh();
      return data.job;
    },
    [refresh]
  );

  const recordPayment = useCallback(
    async ({ jobId, amount }) => {
      await apiJson(`/print-jobs/${jobId}/pay`, {
        method: 'POST',
        body: JSON.stringify({ amount: Number(amount) }),
      });
      await refresh();
    },
    [refresh]
  );

  const savePrintLabSettings = useCallback(
    async ({ rateBw, rateColor, rateCopy, rateScan, rateCustomerReam }) => {
      const data = await apiJson('/owner/print-lab-settings', {
        method: 'PATCH',
        body: JSON.stringify({
          rateBw: roundMoney2(rateBw),
          rateColor: roundMoney2(rateColor),
          rateCopy: roundMoney2(rateCopy),
          rateScan: roundMoney2(rateScan),
          rateCustomerReam: roundMoney2(rateCustomerReam),
        }),
      });
      await refresh();
      return data.settings;
    },
    [refresh]
  );

  const stats = useMemo(() => {
    const totalJobs = printJobs.length;
    const totalRevenue = printJobs.reduce((sum, job) => sum + (Number(job.paidAmount) || 0), 0);
    const pendingBalance = printJobs.reduce((sum, job) => sum + (Number(job.balance) || 0), 0);
    return { totalJobs, totalRevenue, pendingBalance };
  }, [printJobs]);

  return (
    <PrintingContext.Provider
      value={{
        printJobs,
        addPrintJob,
        recordPayment,
        savePrintLabSettings,
        stats,
        RATE_BW,
        RATE_COLOR,
        RATE_COPY,
        RATE_SCAN,
        RATE_CUSTOMER_REAM,
        refreshPrintJobs: refresh,
      }}
    >
      {children}
    </PrintingContext.Provider>
  );
}
